/**
 * Setup instruction data for each integration type.
 *
 * Each entry describes the steps a user must complete externally
 * (e.g. creating tokens, finding URLs) before adding a connection.
 */

export interface SetupStep {
  title: string;
  description: string;
  link?: { label: string; url: string };
  code?: string;
}

export interface IntegrationSetupData {
  intro: string;
  steps: SetupStep[];
  tip?: string;
}

export const integrationSetupData: Record<string, IntegrationSetupData> = {
  github: {
    intro: 'Connect LeftShield to GitHub to scan repositories, monitor pull requests, and enforce security policies.',
    steps: [
      {
        title: 'Create a Personal Access Token',
        description: 'Go to GitHub and generate a fine-grained or classic PAT with the required scopes.',
        link: { label: 'GitHub Token Settings', url: 'https://github.com/settings/tokens' },
        code: 'repo, read:org, admin:repo_hook',
      },
      {
        title: 'Enter your organization or username',
        description: 'Provide the GitHub organization or personal account that owns the repositories you want to scan.',
      },
      {
        title: 'Add the connection',
        description: 'Click "Add Connection" above, paste your token, and test the connection to confirm access.',
      },
    ],
    tip: 'For organization-wide access, consider installing the LeftShield GitHub App instead of a personal token.',
  },

  gitlab: {
    intro: 'Connect LeftShield to GitLab to scan repositories and monitor merge requests.',
    steps: [
      {
        title: 'Create a Personal Access Token',
        description: 'In GitLab, navigate to User Settings > Access Tokens and create a token with the required scopes.',
        link: { label: 'GitLab Access Tokens', url: 'https://gitlab.com/-/user_settings/personal_access_tokens' },
        code: 'api, read_repository',
      },
      {
        title: 'Determine your instance URL',
        description: 'For GitLab.com use the default URL. For self-hosted instances, enter the full base URL of your GitLab server.',
        code: 'https://gitlab.com  or  https://gitlab.yourcompany.com',
      },
      {
        title: 'Add the connection',
        description: 'Click "Add Connection" above, enter your instance URL and token, then test the connection.',
      },
    ],
  },

  'azure-devops': {
    intro: 'Connect LeftShield to Azure DevOps to scan repositories and monitor pull requests.',
    steps: [
      {
        title: 'Create a Personal Access Token',
        description: 'In Azure DevOps, go to User Settings > Personal Access Tokens and create a new token.',
        link: { label: 'Azure DevOps Tokens', url: 'https://dev.azure.com' },
        code: 'Code (Read), Project and Team (Read)',
      },
      {
        title: 'Find your organization URL',
        description: 'Your organization URL is in the format shown below. You can find it in the address bar of your Azure DevOps portal.',
        code: 'https://dev.azure.com/your-organization',
      },
      {
        title: 'Add the connection',
        description: 'Click "Add Connection" above, enter your organization URL and PAT, then test the connection.',
      },
    ],
  },

  jira: {
    intro: 'Connect LeftShield to Jira to automatically create issues from security findings.',
    steps: [
      {
        title: 'Create an API Token',
        description: 'Go to your Atlassian account security settings and generate a new API token.',
        link: { label: 'Atlassian API Tokens', url: 'https://id.atlassian.com/manage-profile/security/api-tokens' },
      },
      {
        title: 'Find your Jira Cloud URL',
        description: 'Your Jira Cloud instance URL follows the format below. Your username is the email address associated with your Atlassian account.',
        code: 'https://your-domain.atlassian.net',
      },
      {
        title: 'Add the connection',
        description: 'Click "Add Connection" above, enter your instance URL, email, and API token, then test the connection.',
      },
    ],
    tip: 'Your Jira username must be the email address linked to your Atlassian account, not a display name.',
  },

  servicenow: {
    intro: 'Connect LeftShield to ServiceNow to create incidents from security findings.',
    steps: [
      {
        title: 'Identify your instance URL',
        description: 'Your ServiceNow instance URL follows the format below. Contact your ServiceNow admin if you are unsure.',
        code: 'https://your-instance.service-now.com',
      },
      {
        title: 'Create a service account',
        description: 'Create a dedicated service account in ServiceNow with the required roles for incident management.',
        code: 'Roles: itil, sn_incident_write',
      },
      {
        title: 'Add the connection',
        description: 'Click "Add Connection" above, enter your instance URL and service account credentials, then test the connection.',
      },
    ],
  },

  snyk: {
    intro: 'Connect LeftShield to Snyk to import vulnerability data from your Snyk organization.',
    steps: [
      {
        title: 'Find your Snyk Organization ID',
        description: 'In the Snyk dashboard, go to Settings > General to find your Organization ID.',
        link: { label: 'Snyk Dashboard', url: 'https://app.snyk.io' },
      },
      {
        title: 'Create an API Token',
        description: 'Go to your Snyk account settings and generate a new API token.',
        link: { label: 'Snyk Account Settings', url: 'https://app.snyk.io/account' },
      },
      {
        title: 'Add the connection',
        description: 'Click "Add Connection" above, enter your Organization ID and API token, then test the connection.',
      },
    ],
  },

  notifications: {
    intro: 'Add notification channels to receive alerts when LeftShield detects security issues.',
    steps: [
      {
        title: 'Slack — Create an Incoming Webhook',
        description: 'In your Slack workspace, create an Incoming Webhook via the Slack App Directory and copy the webhook URL.',
        link: { label: 'Slack Incoming Webhooks', url: 'https://api.slack.com/messaging/webhooks' },
        code: 'https://hooks.slack.com/services/T.../B.../...',
      },
      {
        title: 'Microsoft Teams — Add a Webhook Connector',
        description: 'In the target Teams channel, add the "Incoming Webhook" connector and copy the generated URL.',
        code: 'https://your-tenant.webhook.office.com/webhookb2/...',
      },
      {
        title: 'Generic Webhook — Use any HTTP endpoint',
        description: 'Provide any URL that accepts POST requests with a JSON payload. LeftShield will send alert events to this endpoint.',
      },
    ],
    tip: 'You can add multiple channels to receive notifications in different tools simultaneously.',
  },
};
